import { supabase } from '../lib/supabase';
import {
  ManifestGeneratorService,
  type Manifest,
} from './manifestGeneratorService';
import {
  BlueprintGeneratorService,
  type Blueprint,
} from './blueprintGeneratorService';
import { AnthropicService } from './anthropicService';
import { logger } from './loggingService';

export interface JiraEpic {
  title: string;
  description: string;
  priority: 'highest' | 'high' | 'medium' | 'low' | 'lowest';
  stories: JiraStory[];
}

export interface JiraStory {
  title: string;
  description: string;
  acceptanceCriteria: string[];
  storyPoints: number;
  priority: 'highest' | 'high' | 'medium' | 'low' | 'lowest';
  labels: string[];
  tasks: JiraTask[];
}

export interface JiraTask {
  title: string;
  description: string;
  estimatedHours: number;
}

export interface JiraPlan {
  id: string;
  project_id: string;
  manifest_id: string;
  blueprint_id: string;
  epics: JiraEpic[];
  totalStoryPoints: number;
  estimatedWeeks: number;
  created_at: string;
  updated_at: string;
}

export class JiraPlanGeneratorService {
  static async generateFromBlueprint(
    userId: string,
    projectId: string,
    blueprintId: string,
    _jiraProjectKey: string
  ): Promise<JiraEpic[]> {
    try {
      const blueprint =
        await BlueprintGeneratorService.getBlueprint(blueprintId);

      if (!blueprint) {
        throw new Error('Blueprint not found');
      }

      const prompt = `You are an expert Agile project manager. Generate a comprehensive Jira plan (epics and stories) from the following blueprint.

# Blueprint

**Title:** ${blueprint.title}

## Phases
${blueprint.phases.map((p, i) => `${i + 1}. ${p.name} - ${p.description}\n   Deliverables: ${p.deliverables.join(', ')}\n   Duration: ${p.estimatedDays} days`).join('\n\n')}

# Task

Generate a Jira plan with epics and user stories in JSON format.

Provide ONLY a JSON object with an "epics" array, no additional explanation.`;

      const jiraPlanContent = await AnthropicService.generateText(prompt, {
        maxTokens: 8000,
        temperature: 0.6,
        userId: userId,
      });

      const parsedPlan = this.parseAIResponse(jiraPlanContent.content);

      const totalStoryPoints = parsedPlan.epics.reduce(
        (total, epic) =>
          total +
          epic.stories.reduce((sum, story) => sum + story.storyPoints, 0),
        0
      );

      const estimatedWeeks = Math.ceil(totalStoryPoints / 20);

      // Insert with proper error handling (enterprise-grade)
      const { error: insertError } = await supabase.from('work_plans').insert({
        project_id: projectId,
        blueprint_id: blueprintId,
        plan_type: 'jira',
        content: {
          epics: parsedPlan.epics,
          totalStoryPoints,
          estimatedWeeks,
        },
      });

      if (insertError) {
        logger.error('Error saving Jira plan to database', { 
          error: insertError, 
          projectId, 
          blueprintId 
        });
        throw new Error(`Failed to save Jira plan: ${insertError.message}`);
      }

      return parsedPlan.epics;
    } catch (error) {
      logger.error('Jira plan generation failed', { 
        error, 
        projectId, 
        blueprintId,
        userId 
      });
      throw new Error(
        `Failed to generate Jira plan: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  static async generateFromManifest(
    manifestId: string,
    userId: string
  ): Promise<JiraPlan> {
    try {
      const manifest = await ManifestGeneratorService.getManifest(manifestId);

      if (!manifest) {
        throw new Error('Manifest not found');
      }

      const blueprint = await BlueprintGeneratorService.getBlueprint(
        manifest.blueprint_id
      );

      if (!blueprint) {
        throw new Error('Blueprint not found');
      }

      const prompt = this.buildJiraPlanPrompt(blueprint, manifest);

      const jiraPlanContent = await AnthropicService.generateText(prompt, {
        maxTokens: 8000,
        temperature: 0.6,
        userId: userId,
      });

      const parsedPlan = this.parseAIResponse(jiraPlanContent.content);

      const totalStoryPoints = parsedPlan.epics.reduce(
        (total, epic) =>
          total +
          epic.stories.reduce((sum, story) => sum + story.storyPoints, 0),
        0
      );

      const estimatedWeeks = Math.ceil(totalStoryPoints / 20);

      const { data: jiraPlan, error } = await supabase
        .from('work_plans')
        .insert({
          project_id: manifest.project_id,
          manifest_id: manifestId,
          blueprint_id: manifest.blueprint_id,
          plan_type: 'jira',
          content: {
            epics: parsedPlan.epics,
            totalStoryPoints,
            estimatedWeeks,
          },
        })
        .select()
        .maybeSingle();

      if (error) {
        logger.error('Error inserting Jira plan', { error, projectId: manifest.project_id, manifestId, blueprintId: manifest.blueprint_id });
        throw error;
      }

      if (!jiraPlan) {
        throw new Error('Failed to create Jira plan - no data returned');
      }

      return {
        id: jiraPlan.id,
        project_id: jiraPlan.project_id,
        manifest_id: manifestId,
        blueprint_id: manifest.blueprint_id,
        epics: parsedPlan.epics,
        totalStoryPoints,
        estimatedWeeks,
        created_at: jiraPlan.created_at,
        updated_at: jiraPlan.updated_at,
      };
    } catch (error) {
      logger.error('Jira plan generation from manifest failed', { 
        error, 
        manifestId, 
        userId 
      });
      throw new Error(
        `Failed to generate Jira plan: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private static buildJiraPlanPrompt(
    blueprint: Blueprint,
    manifest: Manifest
  ): string {
    return `You are an expert Agile project manager. Generate a comprehensive Jira plan (epics and stories) from the following blueprint and manifest.

# Blueprint

**Title:** ${blueprint.title}

## Phases
${blueprint.phases.map((p, i) => `${i + 1}. ${p.name} - ${p.description}\n   Deliverables: ${p.deliverables.join(', ')}\n   Duration: ${p.estimatedDays} days`).join('\n\n')}

# Manifest

## File Structure
${manifest.file_structure
  .slice(0, 10)
  .map(f => `- ${f.path} (${f.type}): ${f.description}`)
  .join('\n')}

## Dependencies
${manifest.dependencies
  .slice(0, 10)
  .map(d => `- ${d.name} ${d.version}: ${d.purpose}`)
  .join('\n')}

## API Endpoints
${manifest.api_endpoints
  .slice(0, 8)
  .map(e => `- ${e.method} ${e.path}: ${e.description}`)
  .join('\n')}

## Database Schema
${manifest.database_schema
  .slice(0, 5)
  .map(t => `- Table: ${t.table} (${t.columns.length} columns)`)
  .join('\n')}

# Task

Generate a Jira plan with epics and user stories in the following JSON structure:

\`\`\`json
{
  "epics": [
    {
      "title": "Epic title",
      "description": "Epic description",
      "priority": "high",
      "stories": [
        {
          "title": "User story title",
          "description": "As a [user type], I want [goal] so that [benefit]",
          "acceptanceCriteria": [
            "Given [context] When [action] Then [outcome]"
          ],
          "storyPoints": 5,
          "priority": "high",
          "labels": ["frontend", "backend"],
          "tasks": [
            {
              "title": "Task title",
              "description": "Task description",
              "estimatedHours": 4
            }
          ]
        }
      ]
    }
  ]
}
\`\`\`

Guidelines:
- Create 3-5 epics aligned with project phases
- Each epic should have 3-7 user stories
- Story points: 1 (trivial), 2 (simple), 3 (medium), 5 (complex), 8 (very complex)
- Each story should have 2-4 tasks
- Use proper Agile user story format
- Prioritize based on dependencies and value

Provide ONLY the JSON object, no additional explanation.`;
  }

  private static parseAIResponse(response: string): {
    epics: JiraEpic[];
  } {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      return {
        epics: parsed.epics || [],
      };
    } catch (error) {
      logger.error('Failed to parse AI response:', error);
      return {
        epics: [
          {
            title: 'Project Setup & Infrastructure',
            description: 'Set up development environment and infrastructure',
            priority: 'highest',
            stories: [
              {
                title: 'Set up development environment',
                description:
                  'As a developer, I want a configured development environment so that I can start coding immediately',
                acceptanceCriteria: [
                  'Given a new machine, when I clone the repo and run setup, then the environment is ready',
                  'Given the environment is set up, when I run tests, then all tests pass',
                ],
                storyPoints: 3,
                priority: 'highest',
                labels: ['infrastructure', 'setup'],
                tasks: [
                  {
                    title: 'Create project structure',
                    description: 'Set up folder structure and initial files',
                    estimatedHours: 2,
                  },
                  {
                    title: 'Configure development tools',
                    description: 'Set up linting, formatting, and testing',
                    estimatedHours: 3,
                  },
                ],
              },
            ],
          },
          {
            title: 'Core Features Development',
            description: 'Implement main application features',
            priority: 'high',
            stories: [
              {
                title: 'Implement user authentication',
                description:
                  'As a user, I want to sign up and log in so that I can access my account',
                acceptanceCriteria: [
                  'Given valid credentials, when I log in, then I can access protected pages',
                  'Given I am logged in, when I log out, then I am redirected to the login page',
                ],
                storyPoints: 5,
                priority: 'high',
                labels: ['auth', 'backend', 'frontend'],
                tasks: [
                  {
                    title: 'Create authentication API',
                    description: 'Build login and signup endpoints',
                    estimatedHours: 6,
                  },
                  {
                    title: 'Build login/signup UI',
                    description: 'Create authentication forms',
                    estimatedHours: 4,
                  },
                ],
              },
            ],
          },
          {
            title: 'Testing & Deployment',
            description: 'Ensure quality and deploy to production',
            priority: 'medium',
            stories: [
              {
                title: 'Write automated tests',
                description:
                  'As a developer, I want comprehensive tests so that I can deploy with confidence',
                acceptanceCriteria: [
                  'Given the application, when tests run, then coverage is above 80%',
                  'Given critical flows, when E2E tests run, then all scenarios pass',
                ],
                storyPoints: 8,
                priority: 'high',
                labels: ['testing', 'quality'],
                tasks: [
                  {
                    title: 'Write unit tests',
                    description: 'Test individual functions and components',
                    estimatedHours: 12,
                  },
                  {
                    title: 'Write integration tests',
                    description: 'Test API endpoints and database interactions',
                    estimatedHours: 8,
                  },
                ],
              },
            ],
          },
        ],
      };
    }
  }

  static async exportToJira(
    jiraPlanId: string,
    jiraProjectKey: string,
    accessToken: string
  ): Promise<{
    success: boolean;
    exportedEpics: number;
    exportedStories: number;
  }> {
    try {
      const { data: workPlan } = await supabase
        .from('work_plans')
        .select('*')
        .eq('id', jiraPlanId)
        .single();

      if (!workPlan || !workPlan.content) {
        throw new Error('Work plan not found');
      }

      const { epics } = workPlan.content as { epics: JiraEpic[] };

      const { data: connection } = await supabase
        .from('user_connections')
        .select('account_id')
        .eq('access_token', accessToken)
        .eq('provider', 'jira')
        .maybeSingle();

      if (!connection || !connection.account_id) {
        throw new Error('Jira cloud ID not found');
      }

      const jiraCloudId = connection.account_id;
      const jiraBaseUrl = `https://api.atlassian.com/ex/jira/${jiraCloudId}/rest/api/3`;

      let exportedEpics = 0;
      let exportedStories = 0;

      for (const epic of epics) {
        const epicResponse = await fetch(`${jiraBaseUrl}/issue`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fields: {
              project: { key: jiraProjectKey },
              summary: epic.title,
              description: epic.description,
              issuetype: { name: 'Epic' },
              priority: { name: this.mapPriority(epic.priority) },
            },
          }),
        });

        if (!epicResponse.ok) {
          logger.error('Failed to create epic:', await epicResponse.text());
          continue;
        }

        const { key: epicKey } = await epicResponse.json();
        exportedEpics++;

        for (const story of epic.stories) {
          const storyResponse = await fetch(`${jiraBaseUrl}/issue`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fields: {
                project: { key: jiraProjectKey },
                summary: story.title,
                description: `${story.description}\n\nAcceptance Criteria:\n${story.acceptanceCriteria.map(ac => `- ${ac}`).join('\n')}`,
                issuetype: { name: 'Story' },
                priority: { name: this.mapPriority(story.priority) },
                parent: { key: epicKey },
                labels: story.labels,
                customfield_10016: story.storyPoints,
              },
            }),
          });

          if (!storyResponse.ok) {
            logger.error(
              'Failed to create story:',
              await storyResponse.text()
            );
            continue;
          }

          exportedStories++;
        }
      }

      return { success: true, exportedEpics, exportedStories };
    } catch (error) {
      logger.error('Jira export failed:', error);
      throw error;
    }
  }

  private static mapPriority(
    priority: 'highest' | 'high' | 'medium' | 'low' | 'lowest'
  ): string {
    const mapping = {
      highest: 'Highest',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      lowest: 'Lowest',
    };
    return mapping[priority] || 'Medium';
  }
}
